import { useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { MessageCircleHeart, Loader2, CheckCircle2, PhoneCall, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COPY = {
  open: { label: 'Request received', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  contacted: { label: 'Team reached out', cls: 'bg-sky-50 text-sky-800 border-sky-200' },
  closed: { label: 'Closed', cls: 'bg-muted text-muted-foreground' },
};

// V31 — SoulAI Till-Delivery is sales-assisted (OPS payment link). Never a pay button here.
export const TillDeliveryTeamCard = ({ item, enquiry, onEnquiry }) => {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const status = result?.status || enquiry?.status;
  const isOpen = status === 'open' || status === 'contacted';
  const st = status ? STATUS_COPY[status] : null;

  const speakToTeam = async () => {
    setSending(true);
    try {
      const r = await api.post('/api/soulai/speak-to-team', {
        plan: 'soulai_till_delivery', channel: 'paywall', category: 'sales',
        context: {
          product: 'soulai_coach', months: item.months, days_remaining: item.days_remaining,
          discount_pct_shown: item.discount_pct, effective_monthly_shown: item.effective_monthly_amount,
          currency: item.razorpay_currency, display_as: item.display_as, stage: 'prenatal', platform: 'web',
        },
      });
      setResult(r.data);
      toast.success(r.data.message);
      onEnquiry?.(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not reach our team. Please try again.'); }
    setSending(false);
  };

  return (
    <div className="relative rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 to-rose-50/60 p-4" data-testid="paywall-plan-till_delivery">
      {item.badge && <Badge className="absolute -top-2.5 right-3 bg-amber-100 text-amber-900 border-amber-200 text-[10px]" data-testid="paywall-td-badge">{item.badge}</Badge>}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-heading font-semibold text-sm">Till Delivery</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">Every week until your due date + 3 days · enrolled with our team</p>
          <p className="font-heading text-base font-semibold mt-2 text-primary" data-testid="paywall-td-headline">{item.headline}</p>
          {item.eligible ? (
            <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1" data-testid="paywall-td-window">
              <CalendarDays className="h-3 w-3" />{item.months} {item.months === 1 ? 'month' : 'months'} · access till {item.access_until}
              {item.estimated_total_formatted ? ` · about ${item.estimated_total_formatted} in total` : ''}
            </p>
          ) : (
            <p className="text-[11px] text-amber-800 mt-1" data-testid="paywall-td-ineligible">
              {item.ineligible_reason === 'EDD_REQUIRED' ? 'Add your due date in Profile so our team can quote your plan.' : 'Till Delivery is not available after your due date.'}
            </p>
          )}
        </div>
        <div className="text-right shrink-0">
          <p className="text-[11px] text-muted-foreground line-through">{item.discount_pct > 0 ? item.monthly_ref_formatted : ''}</p>
          <p className="font-heading text-lg font-semibold" data-testid="paywall-price-till_delivery">{item.effective_monthly_formatted}<span className="text-xs font-normal text-muted-foreground">/mo</span></p>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {isOpen ? (
          <>
            <Badge className={`${st.cls} text-[10px]`} data-testid="paywall-td-status"><CheckCircle2 className="h-3 w-3 mr-1" />{st.label}</Badge>
            <span className="text-[11px] text-muted-foreground" data-testid="paywall-td-status-note">
              {status === 'contacted' ? 'Our team has been in touch.' : `Our team will call within ${item.sla_hours || 24} hours (except Sunday).`}
            </span>
            <Button size="sm" variant="ghost" className="h-8 text-xs ml-auto" onClick={speakToTeam} disabled={sending} data-testid="paywall-td-nudge-btn">
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><PhoneCall className="h-3.5 w-3.5 mr-1" />Still waiting?</>}
            </Button>
          </>
        ) : (
          <Button size="sm" className="h-9 w-full" onClick={speakToTeam} disabled={sending || !item.eligible} data-testid="paywall-td-speak-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><MessageCircleHeart className="h-4 w-4 mr-1.5" />Speak to the team</>}
          </Button>
        )}
      </div>
      <p className="text-[10px] text-muted-foreground mt-2">{item.estimate_note}</p>
    </div>
  );
};
